import { Box, BoxProps, Flex, Spinner, useToken } from "@chakra-ui/react";
import { bbox } from "@turf/turf";
import { useContext, useEffect, useMemo, useState } from "react";
import { useMap } from "react-map-gl/maplibre";

import { MapDeckGl } from "@nens/deck-gl";
import { hexToRgb } from "@nens/shared-utils";

import { MapResult } from "@/api";
import { MAP_HEADING_SIZE_PX } from "@/features/report/components/report-block";
import { ProjectContext } from "@/stores/project-provider";

import { useReportMap } from "../hooks/use-report-map";

interface ReportMapProps extends BoxProps {
  mapId: string;
  mapResult: MapResult | undefined;
  mapBorderBottom?: number;
}

export const REPORT_MAP_PADDING_Y = 10;
export const REPORT_MAP_PADDING_X = 20;

export function ReportMap({
  children,
  mapId,
  mapResult,
  mapBorderBottom,
  ...rest
}: ReportMapProps) {
  const { project } = useContext(ProjectContext);
  const [isLoading, setIsLoading] = useState(true);
  const map = useMap()[mapId];
  const { layers, mapStyle, computeScale, scaleWidthPx, scaleText } = useReportMap(
    mapId,
    mapResult
  );

  const [bg] = useToken("colors", ["brand.bg"]);
  const bgRgb = hexToRgb(bg);

  const bounds = useMemo(
    () =>
      project.area && project.area.type === "Polygon"
        ? (bbox(project.area as GeoJSON.Polygon).splice(0, 4) as [number, number, number, number])
        : null,
    [project.area]
  );

  const fitBoundsOptions = useMemo(
    () => ({
      padding: {
        top: MAP_HEADING_SIZE_PX + REPORT_MAP_PADDING_Y,
        bottom: mapBorderBottom ?? REPORT_MAP_PADDING_Y,
        left: REPORT_MAP_PADDING_X,
        right: REPORT_MAP_PADDING_X,
      },
    }),
    [mapBorderBottom]
  );

  useEffect(() => {
    if (!map || !bounds) return;
    map.fitBounds(bounds, fitBoundsOptions);
  }, [bounds, map, fitBoundsOptions]);

  if (!bounds || !mapStyle) return null;

  return (
    <Box {...rest} h="full" position="relative" pointerEvents="none">
      {scaleWidthPx && scaleText && (
        <Box
          px={2}
          border="1px solid black"
          borderTop={0}
          zIndex={9999}
          position="absolute"
          bottom={2}
          right={2}
          w={scaleWidthPx + "px"}
          color="black"
          bg="rgba(255, 255, 255, 0.8)"
          fontSize="xs"
          lineHeight="1.1"
          textAlign="center"
          pointerEvents="none"
        >
          {scaleText}
        </Box>
      )}
      {isLoading && (
        <Flex
          zIndex={2}
          justifyContent="center"
          alignItems="center"
          w="full"
          h="full"
          position="absolute"
          bg={`rgba(${bgRgb}, 0.8)`}
        >
          <Spinner size="xl" />
        </Flex>
      )}
      <MapDeckGl
        id={mapId}
        mapProps={{
          onLoad: () => {
            computeScale();
            setIsLoading(false);
          },
          mapStyle,
          initialViewState: {
            bounds,
            fitBoundsOptions,
          },
          interactive: false,
          attributionControl: false,
        }}
        deckGlProps={{
          // If not interleaved layers are removed on browser zoom
          interleaved: true,
          layers,
        }}
      >
        {children}
      </MapDeckGl>
    </Box>
  );
}
